import Axios from "axios";
import axiosRetry from "axios-retry";
import { apiUrl, apiTranslationsUrl } from "const";
import qs from "qs"; // query string for paramsSerializer
import { cache } from "react";

const createInstance = (url: string) => {
  const axios = Axios.create({
    baseURL: url,
    paramsSerializer: (params) => qs.stringify(params, { indices: false }), // param=value1&param=value2
  });
  axiosRetry(axios, {
    retries: 5,
    retryDelay: (retryCount: number) => {
      // console.log(`retry attempt: ${retryCount}`);
      return 100;
    },
    retryCondition: (error) => {
      return !error?.response || error?.response?.status === 500;
    },
  });
  return axios;
};

export const axios = createInstance(apiUrl);

export class HTTPClientError extends Error {
  public code: number;
  public details: any;

  constructor(code: number, details: any) {
    super();
    this.code = code;
    this.details = details;
    Object.setPrototypeOf(this, HTTPClientError.prototype);
  }
}

class HttpClient {
  private readonly MAX_RETRIES = 5;
  private readonly baseUrl: string;
  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private prepareUrl(path: string, params?: Record<string, any> | null) {
    let url = this.baseUrl + path;
    if (params) {
      url += `?${qs.stringify(params, { indices: false })}`;
    }

    return url;
  }

  private async request(path: string, config: RequestInit | undefined, attempt = 1) {
    let isOk = true;
    let data: any;
    let errorCode: number | undefined;

    try {
      const resp = await fetch(path, config);
      if (!resp.ok) {
        if (resp.status === 500) {
          if (attempt <= this.MAX_RETRIES) {
            return new Promise((resolve) => {
              setTimeout(() => {
                resolve(this.request(path, config, attempt + 1));
              }, 100);
            });
          }
        }
        isOk = false;
        errorCode = resp.status;
      }
      data = await resp.json();
    } catch (err) {
      if (attempt <= this.MAX_RETRIES) {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(this.request(path, config, attempt + 1));
          }, 100);
        });
      }
      throw err;
    }

    if (isOk) {
      return data;
    } else {
      throw new HTTPClientError(errorCode || 500, data);
    }
  }

  public async get<T = any>(
    path: string,
    requestConfig?: Record<string, any> | null,
    nextConfig?: RequestInit
  ): Promise<T> {
    const url = this.prepareUrl(path, requestConfig?.params || null);
    const addConfig = nextConfig || {};

    return await this.request(url, { ...nextConfig, ...addConfig });
  }
}

export const api = new HttpClient(apiUrl);
export const apiTr = new HttpClient(apiTranslationsUrl);
